import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSElement,
  WSFlexBox,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IPayeeEngagementResponse,
  IPayeeResponse
} from "@wingspanhq/payments/dist/interfaces";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { FieldView } from "../../../shared/components/FieldView/FieldView";
import React from "react";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import { useDeletePayeeEngagement } from "../../../query/payeeEngagements/mutations/useDeletePayeeEngagement";
import { ErrorContextKey } from "../../../services/platform";

export interface DeleteEngagementModalProps {
  payee: IPayeeResponse;
  engagement: IPayeeEngagementResponse;
  onClose: () => void;
}

export const DeleteEngagementModal: React.FC<DeleteEngagementModalProps> = ({
  payee,
  engagement,
  onClose
}) => {
  const { openSnackbar } = useWSSnackbar();
  const [deleteEngagement, deleteEngagementMeta] = useDeletePayeeEngagement();

  const handleDelete = async () => {
    await deleteEngagement(
      {
        payeeId: payee.payeeId,
        engagementId: engagement.payerPayeeEngagementId
      },
      {
        onSuccess: () => {
          openSnackbar({
            theme: "dark",
            type: "success",
            message: "Contractor engagement deleted"
          });
          onClose();
        }
      }
    );
  };

  const payeeNames = wsName({
    user: payee.user!,
    member: payee.member
  });

  return (
    <>
      <WSText.ParagraphSm mb="L" weight="book">
        Are you sure you want to delete this contractor engagement?
      </WSText.ParagraphSm>
      <WSPanel>
        <WSFlexBox gap="XL">
          <FieldView
            label="Engagement name"
            value={engagement.engagementName}
          />
          <FieldView
            label="Contractor name"
            value={payeeNames.getResolvedName()}
          />
        </WSFlexBox>
      </WSPanel>

      <WSAlert
        my="2XL"
        theme="warning"
        icon="alert"
        size="M"
        title="Deleting contractor engagement"
      >
        Deletion is a permanent action, it cannot be restored.
      </WSAlert>

      <WSErrorMessage
        error={deleteEngagementMeta.error}
        mb="XL"
        contextKey={ErrorContextKey.Engagement}
      />

      <WSActions
        alignment="fill"
        sortButtons={false}
        buttons={[
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Secondary"
          },
          {
            label: "Delete contractor engagement",
            loading: deleteEngagementMeta.isLoading,
            onAsyncClick: handleDelete,
            kind: "Primary",
            destructive: true
          }
        ]}
      />
    </>
  );
};

export const useDeleteEngagementModal = () => {
  return useWSModal(DeleteEngagementModal, {
    title: "Delete contractor engagement",
    size: "M"
  });
};
